import { useState, useMemo } from "react";
import { X, Search, Check } from "lucide-react";
import { ALL_PIZZAS } from "@/data/menu";

const formatBRL = (v) => v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

/**
 * MeioMeioModal — user picks 2 flavors of same type (salgada or doce).
 * After confirm, callback onPicked({a, b}) triggers BordaModal via parent.
 */
export const MeioMeioModal = ({ open, onClose, onPicked }) => {
  const [type, setType] = useState("salgada");
  const [q, setQ] = useState("");
  const [a, setA] = useState(null);
  const [b, setB] = useState(null);

  const pool = useMemo(
    () =>
      ALL_PIZZAS.filter((p) => p.flavorType === type).filter((p) =>
        q.trim() ? p.name.toLowerCase().includes(q.trim().toLowerCase()) : true
      ),
    [type, q]
  );

  const canConfirm = a && b && a.name !== b.name;

  const pick = (p) => {
    if (!a) return setA(p);
    if (!b && p.name !== a.name) return setB(p);
    // If clicking an already-selected item, deselect it
    if (a?.name === p.name) return setA(b), setB(null);
    if (b?.name === p.name) return setB(null);
    // Otherwise replace b
    setB(p);
  };

  const resetAndClose = () => {
    setA(null);
    setB(null);
    setQ("");
    onClose();
  };

  const confirm = () => {
    if (!canConfirm) return;
    // Higher price flavor becomes the "base" (its category tier determines pricing)
    const priceRef = (p) => (p.prices?.["8f"] ?? 0);
    const [base, other] = priceRef(a) >= priceRef(b) ? [a, b] : [b, a];
    onPicked({ base, other });
    resetAndClose();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[80] flex items-end" data-testid="meio-modal">
      <div className="absolute inset-0 bg-black/75 backdrop-blur-sm" onClick={resetAndClose} />
      <div className="relative w-full max-w-md mx-auto bg-zinc-950 border-t border-zinc-800 rounded-t-3xl h-[92vh] flex flex-col shadow-2xl animate-fade-up">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-zinc-800">
          <div>
            <p className="text-[11px] uppercase tracking-widest text-rose-400 font-semibold">Monte sua pizza</p>
            <h3 className="font-display text-xl">Meio a Meio</h3>
          </div>
          <button
            onClick={resetAndClose}
            data-testid="meio-close-btn"
            className="w-9 h-9 rounded-full bg-zinc-900 border border-zinc-800 flex items-center justify-center"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Type toggle */}
        <div className="px-4 pt-3">
          <div className="grid grid-cols-2 gap-2" data-testid="meio-type-picker">
            {[
              { id: "salgada", label: "Salgadas" },
              { id: "doce", label: "Doces" },
            ].map((t) => (
              <button
                key={t.id}
                onClick={() => {
                  setType(t.id);
                  setA(null);
                  setB(null);
                }}
                data-testid={`meio-type-${t.id}`}
                className={`text-xs font-semibold py-2 rounded-xl border press ${
                  type === t.id
                    ? "bg-rose-600 border-rose-600 text-white"
                    : "bg-zinc-900 border-zinc-800 text-zinc-300"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* Selected pills */}
        <div className="px-4 mt-3 grid grid-cols-2 gap-2 text-[11px]">
          <SlotPill label="1º sabor" pizza={a} testid="meio-slot-a" />
          <SlotPill label="2º sabor" pizza={b} testid="meio-slot-b" />
        </div>

        {/* Search */}
        <div className="px-4 mt-3">
          <div className="flex items-center gap-2 bg-zinc-900 border border-zinc-800 rounded-xl px-3 py-2">
            <Search className="w-4 h-4 text-zinc-500" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="meio-search"
              placeholder="Buscar sabor..."
              className="flex-1 bg-transparent text-sm outline-none placeholder:text-zinc-600"
            />
          </div>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2" data-testid="meio-flavors-list">
          {pool.map((p) => {
            const isA = a?.name === p.name;
            const isB = b?.name === p.name;
            const selected = isA || isB;
            const badge = isA ? "1" : isB ? "2" : null;
            return (
              <button
                key={p.name}
                onClick={() => pick(p)}
                data-testid={`meio-flavor-${p.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className={`w-full flex items-center gap-3 p-2.5 rounded-xl border press text-left ${
                  selected
                    ? "bg-rose-600/15 border-rose-600"
                    : "bg-zinc-900 border-zinc-800"
                }`}
              >
                <div className="w-12 h-12 rounded-lg overflow-hidden bg-zinc-800 shrink-0">
                  <img src={p.image} alt="" className="w-full h-full object-cover" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold truncate">{p.name}</p>
                  <p className="text-[11px] text-zinc-500 truncate">
                    {p.tier} · 8f {formatBRL(p.prices["8f"])}
                  </p>
                </div>
                {badge && (
                  <div className="w-6 h-6 rounded-full bg-rose-600 text-white text-xs font-bold flex items-center justify-center">
                    {badge}
                  </div>
                )}
              </button>
            );
          })}
          {pool.length === 0 && (
            <p className="text-center text-zinc-500 text-sm py-8">Nenhum sabor encontrado.</p>
          )}
        </div>

        <div className="border-t border-zinc-800 p-4">
          <button
            onClick={confirm}
            disabled={!canConfirm}
            data-testid="meio-confirm-btn"
            className={`press w-full flex items-center justify-center gap-2 font-semibold py-3.5 rounded-2xl ${
              canConfirm
                ? "bg-gradient-to-b from-rose-500 to-rose-600 text-white shadow-[0_10px_30px_-10px_rgba(244,63,94,0.7)]"
                : "bg-zinc-900 text-zinc-600 border border-zinc-800"
            }`}
          >
            <Check className="w-4 h-4" />
            {canConfirm ? "Escolher tamanho e borda" : "Escolha 2 sabores"}
          </button>
        </div>
      </div>
    </div>
  );
};

const SlotPill = ({ label, pizza, testid }) => (
  <div
    className={`rounded-xl border px-3 py-2 ${
      pizza ? "border-rose-600/60 bg-rose-600/10" : "border-dashed border-zinc-700 bg-zinc-900/60"
    }`}
    data-testid={testid}
  >
    <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">{label}</p>
    <p className="text-sm font-semibold text-zinc-100 mt-0.5 truncate">
      {pizza ? pizza.name : "—"}
    </p>
  </div>
);
